import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { API } from "@/lib/api";

const BrandingCtx = createContext(null);
export const useBranding = () => useContext(BrandingCtx);
const BASE = process.env.REACT_APP_BACKEND_URL;

export function hexToHsl(hex) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const SHADCN = { primary: "primary", primary_text: "primary-foreground", secondary: "secondary", secondary_text: "secondary-foreground",
  accent: "accent", background: "background", text: "foreground", card_bg: "card", card_border: "border", input_border: "input",
  input_focus: "ring", muted_text: "muted-foreground", error: "destructive" };

/** Applies a theme (hex map) as CSS variables on an element; invalid values are skipped so defaults remain. */
export function applyTheme(theme, el = document.documentElement) {
  Object.entries(theme || {}).forEach(([k, v]) => {
    if (!/^#[0-9a-f]{6}$/i.test(v)) return;
    el.style.setProperty(`--c-${k.replace(/_/g, "-")}`, v);
    if (SHADCN[k]) el.style.setProperty(`--${SHADCN[k]}`, hexToHsl(v));
  });
}

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/branding`);
      const d = await r.json();
      applyTheme(d.theme);
      document.title = d.identity.platform_name;
      if (d.assets.favicon) {
        let link = document.querySelector("link[rel='icon']") || Object.assign(document.createElement("link"), { rel: "icon" });
        link.href = BASE + d.assets.favicon;
        document.head.appendChild(link);
      }
      setBrand(d);
    } catch (e) {
      setBrand({ identity: { platform_name: "CASURECO II Management Portal", short_name: "CASURECO II" }, theme: {}, assets: {}, general: {} });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const assetUrl = (kind) => (brand?.assets?.[kind] ? BASE + brand.assets[kind] : null);
  return <BrandingCtx.Provider value={{ brand, reload: load, assetUrl }}>{children}</BrandingCtx.Provider>;
}

export function BrandLogo({ kind = "logo_main", className = "h-9 w-9" }) {
  const { assetUrl } = useBranding();
  const url = assetUrl(kind) || assetUrl("logo_main");
  if (url) return <img src={url} alt="Logo" className={`${className} object-contain`} data-testid="brand-logo" />;
  return (
    <div className={`${className} rounded-md grid place-items-center font-display font-bold text-sm`} data-testid="brand-logo-default"
      style={{ background: "var(--c-primary, #D97706)", color: "var(--c-primary-text, #fff)" }}>C2</div>
  );
}

export function BrandFooter() {
  const { brand } = useBranding();
  const i = brand?.identity;
  if (!i || (!i.footer_text && !i.copyright_text && !i.owner_name)) return null;
  return (
    <footer className="text-xs px-6 py-4 border-t" data-testid="app-footer" style={{ background: "var(--c-footer-bg)", color: "var(--c-footer-text)" }}>
      <p>{i.footer_text || i.owner_name}{i.office_address ? ` · ${i.office_address}` : ""}{i.contact_email ? ` · ${i.contact_email}` : ""}{i.contact_phone ? ` · ${i.contact_phone}` : ""}</p>
      {i.copyright_text && <p className="mt-1">{i.copyright_text}</p>}
    </footer>
  );
}
